import { type EventSubscription, subscribeToEvents } from "@storytell/client";
import { dispatchGlobalEvent, isNetworkError } from "@storytell/events";
import type { ProjectId, ServerEvent } from "@storytell/types";
import { createEffect, createMemo, on, onCleanup } from "solid-js";
import { isServer } from "solid-js/web";

interface EventsServiceOptions {
  /**
   * Reactive accessor for the current project ID.
   * When this changes, the service will reconnect to the new project's events.
   */
  projectId: () => ProjectId | undefined;
  /**
   * Reactive accessor for the auth token.
   * Required for authenticating with the events endpoint.
   */
  authToken: () => string | undefined;
}

/**
 * Creates a service that maintains an SSE connection to the server events endpoint.
 *
 * The service:
 * - Automatically connects when a valid projectId is available
 * - Reconnects when the projectId changes
 * - Dispatches "server-event-received" global events for each received server event
 * - Only runs on the client (no-op on server)
 *
 * @example
 * ```ts
 * createEventsService({
 *   projectId: () => userContext.get()?.projectId,
 *   authToken: () => auth.token(),
 * });
 *
 * // Listen to events elsewhere in the app
 * subscribeToServerEvent("cp.assets", (serverEvent) => {
 *   // Refetch assets
 * });
 * ```
 */
export function createEventsService(options: EventsServiceOptions): void {
  if (isServer) return;

  let currentSubscription: EventSubscription | null = null;
  const eventQueue: ServerEvent[] = [];
  let isTabActive = typeof document !== "undefined" ? !document.hidden : true;

  const handleVisibilityChange = () => {
    if (typeof document === "undefined") return;

    const wasActive = isTabActive;
    isTabActive = !document.hidden;

    if (!wasActive && isTabActive) {
      while (eventQueue.length > 0) {
        const event = eventQueue.shift();
        if (event) {
          dispatchGlobalEvent("server-event-received", event);
        }
      }
    }
  };

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", handleVisibilityChange);
  }

  const projectId = createMemo(() => (options.authToken() ? options.projectId() : undefined));

  const disconnect = () => {
    if (currentSubscription) {
      const sub = currentSubscription;
      currentSubscription = null;
      sub.close();
    }
  };

  const connect = (pid: ProjectId) => {
    currentSubscription = subscribeToEvents(
      { projectId: pid },
      {
        authToken: options.authToken,
        onEvent: (event) => {
          const serverEvent = event as ServerEvent;

          if (!isTabActive) {
            eventQueue.push(serverEvent);
          } else {
            dispatchGlobalEvent("server-event-received", serverEvent);
          }
        },
        onError: (error) => {
          console.error("[EventsService] Error receiving event:", error);
          if (isNetworkError(error)) {
            dispatchGlobalEvent("connectivity-streaming-error", {
              error: String(error),
              code: undefined,
            });
          }
        },
        onConnect: () => {
          // no-op
        },
        onDisconnect: () => {
          // no-op
        },
      }
    );
  };

  createEffect(
    on(projectId, (pid) => {
      disconnect();

      if (!pid) {
        return;
      }

      connect(pid);

      onCleanup(() => {
        disconnect();
      });
    })
  );

  onCleanup(() => {
    disconnect();
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    }
    eventQueue.length = 0;
  });
}
